<script setup lang="ts">
  import { ref } from 'vue'
  import {
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription
  } from '@gat-ship/vue-components'
  import {
    AgencyStructureProvider,
    AgencyStructureSelect
  } from '@gat-ship/agency'
  import { AgencySettingsView } from '@gat-ship/agency-settings'
  import type { AgencyStructureNode } from '@gat-ship/types'
  import { useAgencySettingsSelection } from '../../composables/useAgencySettingsSelection'

  // The panel has no route param (it is keyed off `?admin=agency-settings`), so
  // the selected agency node lives in a local ref bound to the selector's
  // v-model — the routed `AgencySettingsShell` keeps it in the `:nodeId` route
  // param instead. Both mount the same provider + picker chrome and render the
  // same `AgencySettingsView`. The menu item has no permission, and the routed
  // view carries no access-props, so this panel needs no permission gate.
  const nodeId = ref<string>('')

  const { pickDefaultAgencyNode } = useAgencySettingsSelection()

  /**
   * When the select has finished fetching the agency structure, default to the
   * first agency if the current nodeId isn't valid (covers the initial empty
   * ref). Agency settings are agency-scoped, so prefer the first agency node
   * over a leading group (the routed shell always had an explicit `:nodeId`).
   */
  function onStructureFetched(structure: AgencyStructureNode[]) {
    const next = pickDefaultAgencyNode(structure, nodeId.value)
    if (next != null) {
      nodeId.value = next
    }
  }

  function changeNodeId(value: string | undefined) {
    if (value) {
      nodeId.value = value
    }
  }
</script>

<template>
  <!-- Fetch the full structure on mount (mirrors the agency-structure panel).
       The select still filters its *display* to active agencies; fetching with
       the default include-inactive keeps the data layer populated so the first
       agency is auto-selected and the per-setting cards load a scoped value. -->
  <AgencyStructureProvider fetch-on-mount>
    <!-- pt-3 offsets the modal's -mt-3 under-fade pull; pb-5 keeps the last
         row off the modal edge (the tenant-users panel precedent). -->
    <div class="flex flex-col gap-6 pb-5 pt-3">
      <ModalSection>
        <ModalSectionHeader class="pt-1">
          <ModalSectionHeaderTitle>Agency Settings</ModalSectionHeaderTitle>
          <ModalSectionHeaderDescription>
            Configure per-agency settings including billing and invoicing
            preferences.
          </ModalSectionHeaderDescription>
        </ModalSectionHeader>
        <AgencyStructureSelect
          class="max-w-[250px]"
          data-source="provider"
          fetch-on-mount
          required
          :model-value="nodeId"
          @agency-structure-fetched="onStructureFetched"
          @update:model-value="changeNodeId"
        />
        <AgencySettingsView :node-id="nodeId" />
      </ModalSection>
    </div>
  </AgencyStructureProvider>
</template>
